import React from 'react';
import { Link } from 'react-router-dom';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
        <p className="text-sm text-muted-foreground mb-10">Last Updated: April 1, 2026</p>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-3">1. Agreement to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using the Eventism platform ("Service"), operated by Eventism LLC ("Company", "we", "us"), 
              you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service. 
              These terms apply to all visitors, users, clients, vendors, staffing professionals, and sales agents who 
              access or use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">2. Description of Service</h2>
            <p className="text-muted-foreground leading-relaxed">
              Eventism is an event services marketplace that connects clients with event service providers including 
              caterers, bartenders, entertainers, staffing professionals, and venue rental partners. The platform 
              provides instant quoting, vendor matching, event planning tools, and payment processing. We act as an 
              intermediary platform facilitating transactions between clients and service providers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">3. User Accounts</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              To access certain features, you must create an account. You agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Provide accurate, current, and complete registration information</li>
              <li>Maintain and update your information to keep it accurate</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We reserve the right to suspend or terminate accounts that violate these terms or engage in 
              fraudulent activity.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">4. Booking and Payment</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              When you book services through Eventism:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>Pricing:</strong> All prices are displayed in US Dollars. Prices include the service cost and any applicable platform fees. Tax may be applied at checkout where required by law.</li>
              <li><strong>Payment Processing:</strong> Payments are processed securely through Stripe. By placing an order, you authorize us to charge your payment method for the total amount.</li>
              <li><strong>Confirmation:</strong> A booking is confirmed only after successful payment. You will receive an email confirmation with booking details.</li>
              <li><strong>Service Delivery:</strong> Service providers are responsible for delivering services as described. Eventism coordinates but does not directly perform event services.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">5. Cancellation and Refund Policy</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>More than 14 days before event:</strong> Full refund minus a 5% processing fee</li>
              <li><strong>7-14 days before event:</strong> 50% refund</li>
              <li><strong>Less than 7 days before event:</strong> No refund, unless the service provider cancels</li>
              <li><strong>Provider cancellation:</strong> If a service provider cancels, you will receive a full refund or the option to rebook with an alternative provider</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              To request a cancellation, contact us at{' '}
              <a href="mailto:team@eventism.com" className="text-indigo-500 hover:underline">team@eventism.com</a> or 
              call (720) 907-4044.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">6. Vendor and Service Provider Terms</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              If you register as a vendor or service provider:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>You represent that you have all necessary licenses, permits, and insurance to provide your services</li>
              <li>You agree to fulfill all accepted bookings to the standard described in your profile</li>
              <li>You agree to Eventism's commission structure as outlined in your vendor agreement</li>
              <li>You are responsible for the quality and safety of your services</li>
              <li>You will maintain accurate availability and pricing on the platform</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">7. Staffing Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              Eventism connects event professionals with staffing opportunities. Staff members engaged through the 
              platform are independent contractors, not employees of Eventism. Staff are responsible for their own 
              tax obligations, insurance, and compliance with local labor laws. Eventism does not guarantee 
              continuous work or minimum hours.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">8. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Eventism platform, including its design, logo, text, graphics, and software, is the property of 
              Eventism LLC and is protected by copyright and trademark laws. You may not reproduce, distribute, or 
              create derivative works from any content on the platform without our express written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">9. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              Eventism acts as a marketplace connecting clients with service providers. To the fullest extent 
              permitted by law, Eventism shall not be liable for any indirect, incidental, special, consequential, 
              or punitive damages arising from the use of our platform, the services provided by vendors, or any 
              events facilitated through the platform. Our total liability shall not exceed the amount you paid 
              for the specific service in question.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">10. Dispute Resolution</h2>
            <p className="text-muted-foreground leading-relaxed">
              Any disputes arising from these terms or the use of the Service shall first be addressed through 
              good-faith negotiation. If unresolved, disputes will be settled through binding arbitration in 
              Denver, Colorado, in accordance with the rules of the American Arbitration Association. You agree 
              to waive any right to participate in a class action lawsuit or class-wide arbitration.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">11. Modifications to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to modify these terms at any time. Material changes will be communicated via 
              email or a prominent notice on the platform. Continued use of the Service after changes constitutes 
              acceptance of the updated terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">12. Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These terms shall be governed by and construed in accordance with the laws of the State of Colorado, 
              without regard to its conflict of law principles.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">13. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              For questions about these Terms of Service, contact us at:
            </p>
            <div className="mt-3 text-muted-foreground">
              <p className="font-medium text-foreground">Eventism LLC</p>
              <p>Denver, Colorado</p>
              <p>Email: <a href="mailto:team@eventism.com" className="text-indigo-500 hover:underline">team@eventism.com</a></p>
              <p>Phone: <a href="tel:7209074044" className="text-indigo-500 hover:underline">(720) 907-4044</a></p>
            </div>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t text-center">
          <p className="text-sm text-muted-foreground">
            See also our <Link to="/privacy" className="text-indigo-500 hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
